import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import {
  ChartLineUpIcon, BankIcon, PackageIcon, CoinsIcon, ChartLineIcon, BuildingsIcon, ClockCounterClockwiseIcon,
  ChartBarIcon, FileXlsIcon,
} from "@phosphor-icons/react";
import SalesReports from "./_reports/SalesReports";
import CxCReports from "./_reports/CxCReports";
import CollectionsReports from "./_reports/CollectionsReports";
import ProjectionsReports from "./_reports/ProjectionsReports";
import InventoryReports from "./_reports/InventoryReports";
import ConsolidatedReports from "./_reports/ConsolidatedReports";
import HistoricalSalesAnalysis from "./_reports/HistoricalSalesAnalysis";
import SalesDynamicReport from "./reports/SalesDynamicReport";
import { ReportsCatalog } from "./ReportsCenter";

/**
 * REPORTES — acceso único (Reportería + Centro de Reportes, sin repetir).
 * Los reportes del catálogo que ya ofrece una pestaña se omiten en OTROS REPORTES:
 *   VENTAS (agrupar por cliente / ítem / RTC / región / categoría, con margen y ranking)
 *   COMPARATIVO DINÁMICO, HISTORIAL DE VENTAS (historial de compras por cliente),
 *   CUENTAS POR COBRAR (resumen, detalle y evolución por período), INVENTARIO (estado por producto).
 */
const CUBIERTOS = [
  "ventas-dinamico", "ventas-por-cliente", "ventas-por-producto", "ventas-por-rtc", "ventas-por-region",
  "ventas-por-linea", "clientes-alto-valor", "rentabilidad-cliente", "historial-compras-cliente",
  "cxc-resumen", "cxc-detallado", "cxc-evolucion", "estado-inventario-producto",
];

const TABS = [
  { k: "ventas", l: "VENTAS", icon: ChartLineUpIcon },
  { k: "dinamico", l: "COMPARATIVO DINÁMICO", icon: ChartBarIcon },
  { k: "historial", l: "HISTORIAL DE VENTAS", icon: ClockCounterClockwiseIcon },
  { k: "inventario", l: "INVENTARIO", icon: PackageIcon },
  { k: "cxc", l: "CUENTAS POR COBRAR", icon: BankIcon },
  { k: "cobranza", l: "COBRANZA Y GRATIVA", icon: CoinsIcon },
  { k: "proyecciones", l: "PROYECCIONES", icon: ChartLineIcon },
  { k: "consolidado", l: "CONSOLIDADO MULTI-EMPRESA", icon: BuildingsIcon },
  { k: "otros", l: "OTROS REPORTES (EXCEL)", icon: FileXlsIcon },
];

export default function Reports() {
  const [sp, setSp] = useSearchParams();
  const inicial = sp.get("tab");
  const [tab, setTabState] = useState(TABS.some((t) => t.k === inicial) ? inicial : "ventas");
  const setTab = (k) => { setTabState(k); setSp(k === "ventas" ? {} : { tab: k }, { replace: true }); };

  return (
    <Layout title="REPORTES" subtitle="Ventas, historial, inventario, cobranza, finanzas y listas · filtros cruzados y exportación a Excel">
      <div className="flex gap-1 mb-5 border-b border-border/60 overflow-x-auto" data-testid="reports-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.k;
          return (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`reports-tab-${t.k}`}
              className={`px-4 py-2.5 text-sm font-semibold tracking-wide flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap transition ${
                active ? "border-agro-green text-agro-green" : "border-transparent text-text-muted hover:text-text-primary"}`}>
              <Icon size={14} weight={active ? "fill" : "regular"} /> {t.l}
            </button>
          );
        })}
      </div>

      {tab === "ventas" && <SalesReports />}
      {tab === "dinamico" && <SalesDynamicReport />}
      {tab === "historial" && <HistoricalSalesAnalysis />}
      {tab === "inventario" && <InventoryReports />}
      {tab === "cxc" && <CxCReports />}
      {tab === "cobranza" && <CollectionsReports />}
      {tab === "proyecciones" && <ProjectionsReports />}
      {tab === "consolidado" && <ConsolidatedReports />}
      {tab === "otros" && <ReportsCatalog excluir={CUBIERTOS} />}
    </Layout>
  );
}
